export function getMessagingParticipantUserId(input: {
  role?: string | null;
  authUserId?: string | null;
  contextUserId?: string | null;
  asPatientId?: string | null;
  asDoctorId?: string | null;
}) {
  if (input.role === 'patient') {
    return input.asPatientId ?? input.contextUserId ?? input.authUserId ?? null;
  }
  if (input.role === 'doctor') {
    return input.asDoctorId ?? input.contextUserId ?? input.authUserId ?? null;
  }

  return input.authUserId ?? null;
}

export function getMessagingIdentitySource(input: {
  isDoctorControlMode?: boolean;
  role?: string | null;
  authUserId?: string | null;
  contextUserId?: string | null;
  asPatientId?: string | null;
  asDoctorId?: string | null;
  actorRole?: string | null;
  actorAuthUserId?: string | null;
  actorContextUserId?: string | null;
  actorAsPatientId?: string | null;
  actorAsDoctorId?: string | null;
}) {
  return {
    role: input.role ?? null,
    authUserId: input.authUserId ?? null,
    contextUserId: input.contextUserId ?? null,
    asPatientId: input.asPatientId ?? null,
    asDoctorId: input.asDoctorId ?? null,
  };
}

export type MessagingRealtimeRoomCandidate =
  | string
  | {
      id: string;
      unreadCountCache?: number | null;
      unreadCount?: number | null;
      lastActivityAt?: string | null;
      updatedAt?: string | null;
      createdAt?: string | null;
    };

const DEFAULT_MESSAGING_REALTIME_ROOM_LIMIT = 40;

const getCandidateId = (candidate: MessagingRealtimeRoomCandidate) =>
  typeof candidate === 'string' ? candidate : candidate.id;

const getCandidateUnreadCount = (candidate: MessagingRealtimeRoomCandidate) => {
  if (typeof candidate === 'string') return 0;
  return candidate.unreadCountCache ?? candidate.unreadCount ?? 0;
};

const getCandidateActivityMs = (candidate: MessagingRealtimeRoomCandidate) => {
  if (typeof candidate === 'string') return 0;
  const value = candidate.lastActivityAt ?? candidate.updatedAt ?? candidate.createdAt ?? null;
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const getMessagingRealtimeRoomIds = (
  rooms: MessagingRealtimeRoomCandidate[],
  selectedRoomId?: string | null,
  options: { limit?: number } = {}
) => {
  const limit = Math.max(1, options.limit ?? DEFAULT_MESSAGING_REALTIME_ROOM_LIMIT);
  const byId = new Map<string, MessagingRealtimeRoomCandidate>();

  for (const room of rooms) {
    const id = getCandidateId(room);
    if (id) byId.set(id, room);
  }

  const selectedIds = selectedRoomId ? [selectedRoomId] : [];
  const selectedIdSet = new Set(selectedIds);
  const prioritizedRoomIds = [...byId.values()]
    .filter(room => !selectedIdSet.has(getCandidateId(room)))
    .sort((first, second) => {
      const unreadDelta = getCandidateUnreadCount(second) - getCandidateUnreadCount(first);
      if (unreadDelta !== 0) return unreadDelta;
      const activityDelta = getCandidateActivityMs(second) - getCandidateActivityMs(first);
      if (activityDelta !== 0) return activityDelta;
      return getCandidateId(first).localeCompare(getCandidateId(second));
    })
    .map(getCandidateId);

  return [...new Set([...selectedIds, ...prioritizedRoomIds])].slice(0, limit).sort();
};

/**
 * Authorization must not follow the bounded, activity-ordered realtime subscription list.
 * Reordering the latter on every room switch would otherwise recreate the Ably client and
 * force an unnecessary token request.
 */
export const getMessagingAuthorizationRoomIds = (rooms: MessagingRealtimeRoomCandidate[]) =>
  [...new Set(rooms.map(getCandidateId).filter(Boolean))].sort();
